
import React, { useEffect, useState } from 'react';

interface GlobalLoadingFallbackProps {
  isVisible: boolean;
  message?: string;
  onForceVisible?: () => void;
  showProgress?: boolean;
  loadingSteps?: string[];
  currentStep?: number;
}

export const GlobalLoadingFallback: React.FC<GlobalLoadingFallbackProps> = ({
  isVisible,
  message = "Loading LiveTrack...",
  onForceVisible,
  showProgress = false,
  loadingSteps = [],
  currentStep = 0
}) => {
  const [showEmergencyButton, setShowEmergencyButton] = useState(false);
  const [showForceLoad, setShowForceLoad] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Show emergency button after 5 seconds (reduced from 10)
      const emergencyTimer = setTimeout(() => {
        setShowEmergencyButton(true);
      }, 5000);

      // Show force load button after 8 seconds
      const forceTimer = setTimeout(() => {
        setShowForceLoad(true);
      }, 8000);
      
      return () => {
        clearTimeout(emergencyTimer);
        clearTimeout(forceTimer);
      };
    }
  }, [isVisible]);

  const handleReload = () => {
    console.log('Force reloading application...');
    
    // Clear all storage
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.warn('Could not clear storage:', e);
    }
    
    // Force reload with cache bypass
    window.location.href = window.location.pathname + '?force_reload=' + Date.now();
  };

  const handleForceLoad = () => {
    console.log('Force loading dashboard...');
    
    // Set force load flag
    try {
      localStorage.setItem('force_dashboard_load', 'true');
    } catch (e) {
      console.warn('Could not set force load flag:', e);
    }
    
    if (onForceVisible) {
      onForceVisible();
    } else {
      // Navigate directly to dashboard
      window.location.href = '/dashboard?force=true';
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#ffffff',
        zIndex: 9998,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, system-ui, sans-serif'
      }}
      data-testid="global-loading-fallback"
    >
      <div style={{ textAlign: 'center', padding: '2rem', maxWidth: '400px' }}>
        <h1 style={{ 
          fontSize: '2rem', 
          marginBottom: '1rem',
          color: '#1f2937',
          fontWeight: 'bold'
        }}>
          LiveTrack
        </h1>
        
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem auto'
        }} />
        
        <p style={{ 
          color: '#6b7280',
          marginBottom: '1rem',
          fontSize: '1rem'
        }}>
          {message}
        </p>

        {showProgress && loadingSteps.length > 0 && (
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            {loadingSteps.map((step, index) => (
              <div key={index} style={{
                padding: '0.5rem',
                fontSize: '0.875rem',
                color: index <= currentStep ? '#059669' : '#6b7280',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: index <= currentStep ? '#059669' : '#e5e7eb',
                  display: 'inline-block'
                }} />
                {step}
              </div>
            ))}
          </div>
        )}

        {showEmergencyButton && (
          <div style={{ marginTop: '2rem' }}>
            <p style={{ 
              color: '#dc2626', 
              fontSize: '0.875rem',
              marginBottom: '1rem'
            }}>
              Taking longer than expected...
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {showForceLoad && (
                <button
                  onClick={handleForceLoad}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#059669',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Force Load Dashboard
                </button>
              )}
              
              <button
                onClick={() => {
                  console.log('Force visible clicked');
                  onForceVisible?.();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Force Visible
              </button>
              
              <button
                onClick={handleReload}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Reload App
              </button>
            </div>
          </div>
        )}
        
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};
